import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MoveLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useGetHelpDeskDetails } from 'services/help-desk/hooks';
import Details from './Details';
import Comments from './Comments';
import { Button } from 'components/ui/button';
import { PATH_ROUTES } from 'routes/path';
import ClosingTicketDialog from './ClosingTicketDialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import Loader from 'components/Loader';
import { HelpDeskTicketDetail } from 'services/help-desk/type';

export default function HelpDeskTicketDetailsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { ticketId } = useParams<{ ticketId: string }>();

  const [ticketDetails, setTicketDetails] = useState<HelpDeskTicketDetail>();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { data, isFetching } = useGetHelpDeskDetails(ticketId ?? '');

  useEffect(() => {
    if (data) setTicketDetails(data);
  }, [data]);

  if (!ticketId) return null;

  if (isFetching) {
    return <Loader />;
  }

  return (
    <>
      <div className='p-4 pt-6 bg-white rounded-3xl'>
        <Button
          onClick={() => navigate(PATH_ROUTES.HELP_DESK)}
          className='flex items-center justify-center gap-2 bg-transparent cursor-pointer hover:bg-transparent text-primary/50 hover:text-primary/50'
        >
          <span>
            <MoveLeft className='w-6 h-6' />
          </span>
          {t('back')}
        </Button>
        <div className='absolute flex justify-end right-2 top-6'>
          <Button
            onClick={() => setIsOpen(true)}
            disabled={ticketDetails?.ticket_detail[0].status === 'closed'}
            className='px-6 h-9'
          >
            {ticketDetails?.ticket_detail[0].status === 'closed'
              ? t('resolved')
              : t('mark_as_resolved')}
          </Button>
        </div>
        <div className='hidden mt-4 md:grid md:grid-cols-2 md:gap-10'>
          {ticketDetails && (
            <>
              <Details ticketDetails={ticketDetails} />
              <Comments ticketDetails={ticketDetails} ticketId={ticketId} />
            </>
          )}
        </div>
        <div className='block mt-4 md:hidden'>
          <Tabs defaultValue='details' className='w-full '>
            <TabsList className='flex justify-around bg-transparent'>
              <TabsTrigger
                value='details'
                className='data-[state=active]:border-primary data-[state=active]:border-b-2 bg-transparent rounded'
              >
                {t('ticket_details')}
              </TabsTrigger>
              <TabsTrigger
                value='comments'
                className='data-[state=active]:border-primary data-[state=active]:border-b-2 bg-transparent rounded'
              >
                {t('comments')}
              </TabsTrigger>
            </TabsList>
            {ticketDetails && (
              <>
                <TabsContent value='details'>
                  <Details ticketDetails={ticketDetails} />
                </TabsContent>
                <TabsContent value='comments'>
                  <Comments ticketDetails={ticketDetails} ticketId={ticketId} />
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </div>

      <ClosingTicketDialog open={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}
